import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  BarChart2, CheckCircle, AlertTriangle, XCircle, Clock,
  MapPin, Cpu, Calendar, Filter, X, ChevronDown, ChevronUp
} from 'lucide-react';
import { api } from '../services/api';

// ── constants ─────────────────────────────────────────────────────────────────

const CATEGORIES   = ['ALARM','SYSTEM','MAINTENANCE','POWER','COMMUNICATION','DEVICE','VIDEO_ALARM','ACCESS','DURESS'];
const SEVERITIES   = ['HIGH','MEDIUM','WARNING','INFO','UNKNOWN'];
const ALARM_STATUSES = ['CONFIRMED','FALSE','PENDING','DEFAULT'];

const PERIODS = [
  { label: 'Last 24 h',    value: 'DAY' },
  { label: 'Last 7 days',  value: 'WEEK' },
  { label: 'Last 30 days', value: 'MONTH' },
  { label: 'Last 3 months', value: '3MONTHS' },
  { label: 'Last 12 months', value: '12MONTHS' },
  { label: 'All Time',       value: 'ALL' },
];

const EMPTY_FILTERS = { province: '', city: '', accountNumber: '', category: '', severity: '', alarmStatus: '' };

const FILTER_LABELS = {
  province: 'Province', city: 'City', accountNumber: 'Panel ID',
  category: 'Category', severity: 'Severity', alarmStatus: 'Alarm Status',
};

// ── helpers ───────────────────────────────────────────────────────────────────

const fmt = (n) => (n ?? 0).toLocaleString();

const fmtMs = (ms) => {
  if (!ms) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 60)  return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60)  return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

const pct = (part, total) => total > 0 ? ((part / total) * 100).toFixed(1) : '0.0';

const getFromDate = (period) => {
  const now = new Date();
  switch (period) {
    case 'DAY':     { const d = new Date(now); d.setDate(now.getDate() - 1);   return d.toISOString(); }
    case 'WEEK':    { const d = new Date(now); d.setDate(now.getDate() - 7);   return d.toISOString(); }
    case 'MONTH':   { const d = new Date(now); d.setMonth(now.getMonth() - 1); return d.toISOString(); }
    case '3MONTHS':  { const d = new Date(now); d.setMonth(now.getMonth() - 3);  return d.toISOString(); }
    case '12MONTHS': { const d = new Date(now); d.setFullYear(now.getFullYear() - 1); return d.toISOString(); }
    default:         return null;
  }
};

const categoryColor = (cat) => ({
  ALARM:'bg-red-500', VIDEO_ALARM:'bg-orange-500', DURESS:'bg-purple-500',
  ACCESS:'bg-indigo-500', POWER:'bg-yellow-500', COMMUNICATION:'bg-blue-500',
  SYSTEM:'bg-cyan-500', MAINTENANCE:'bg-teal-500', DEVICE:'bg-gray-500',
}[cat] || 'bg-gray-400');

const severityColor = (sev) => ({
  HIGH:'bg-red-500', MEDIUM:'bg-orange-400', WARNING:'bg-yellow-400',
  INFO:'bg-blue-400', UNKNOWN:'bg-gray-400',
}[sev] || 'bg-gray-400');

const statusColor = (s) => ({
  CONFIRMED:'bg-red-500', FALSE:'bg-green-500', PENDING:'bg-yellow-500', DEFAULT:'bg-gray-400',
}[s] || 'bg-gray-400');

// ── sub-components ────────────────────────────────────────────────────────────

const StatCard = ({ icon: Icon, label, value, sub, color = 'blue' }) => {
  const colors = {
    blue:   'bg-blue-50   text-blue-700   border-blue-200',
    green:  'bg-green-50  text-green-700  border-green-200',
    red:    'bg-red-50    text-red-700    border-red-200',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    gray:   'bg-gray-50   text-gray-700   border-gray-200',
  };
  return (
    <div className={`rounded-lg border p-4 flex items-start gap-3 ${colors[color]}`}>
      <Icon className="h-6 w-6 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-xs font-medium uppercase tracking-wide opacity-70">{label}</p>
        <p className="text-2xl font-bold">{value}</p>
        {sub && <p className="text-xs mt-0.5 opacity-70">{sub}</p>}
      </div>
    </div>
  );
};

// Clickable bar row — clicking it drills down via onDrillDown
const BarRow = ({ label, count, total, colorClass = 'bg-blue-500', onDrillDown }) => (
  <div
    className={`flex items-center gap-3 py-1.5 rounded px-1 ${onDrillDown ? 'cursor-pointer hover:bg-gray-50' : ''}`}
    onClick={onDrillDown}
    title={onDrillDown ? `Filter by "${label}"` : undefined}
  >
    <span className="w-40 text-sm text-gray-600 truncate flex-shrink-0">{label || '—'}</span>
    <div className="flex-1 bg-gray-100 rounded-full h-3">
      <div className={`${colorClass} h-3 rounded-full transition-all`} style={{ width: `${pct(count, total)}%` }} />
    </div>
    <span className="w-24 text-sm text-right text-gray-700 font-medium">
      {fmt(count)} <span className="text-gray-400 font-normal">({pct(count, total)}%)</span>
    </span>
    {onDrillDown && <Filter className="h-3 w-3 text-gray-300 flex-shrink-0" />}
  </div>
);

const SectionTitle = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2 mb-3">
    <Icon className="h-5 w-5 text-gray-500" />
    <h2 className="text-base font-semibold text-gray-800">{title}</h2>
  </div>
);

const SelectFilter = ({ label, value, options, onChange, placeholder }) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="text-sm border border-gray-200 rounded-md px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
    >
      <option value="">{placeholder || `All ${label}s`}</option>
      {options.map((o) => (
        <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
      ))}
    </select>
  </div>
);

// ── main component ────────────────────────────────────────────────────────────

export const Reports = () => {
  const [period,      setPeriod]      = useState('12MONTHS');
  const [filters,     setFilters]     = useState(EMPTY_FILTERS);
  const [data,        setData]        = useState(null);
  const [options,     setOptions]     = useState({ provinces: [], cities: [], panels: [] });
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Load filter options once (unfiltered, widest time window)
  const optionsLoaded = useRef(false);
  useEffect(() => {
    if (optionsLoaded.current) return;
    optionsLoaded.current = true;
    api.getReports(null, null, {}).then((res) => {
      if (!res.success) return;
      const d = res.data;
      setOptions({
        provinces: (d.byProvince || []).filter(r => r._id).map(r => r._id).sort(),
        cities:    (d.byCity    || []).filter(r => r._id).map(r => r._id).sort(),
        panels:    (d.byPanel   || []).filter(r => r._id).map(r => ({ value: r._id, label: r._id })),
      });
    }).catch(() => {});
  }, []);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const from = getFromDate(period);
      const res = await api.getReports(from, null, filters);
      if (res.success) setData(res.data);
      else setError('Failed to load report data.');
    } catch (err) {
      setError('Error fetching reports: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [period, filters]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  // Apply a single filter dimension (drill-down from chart clicks)
  const drillDown = (key, value) => {
    setFilters(f => ({ ...f, [key]: f[key] === value ? '' : value }));
  };

  const clearFilter = (key) => setFilters(f => ({ ...f, [key]: '' }));
  const clearAll    = () => setFilters(EMPTY_FILTERS);

  const activeFilters = Object.entries(filters).filter(([, v]) => v);
  const activeCount   = activeFilters.length;

  // ── derived values ──────────────────────────────────────────────────────────
  const summary      = data?.summary?.[0]    || {};
  const total        = summary.total         || 0;
  const acknowledged = summary.acknowledged  || 0;
  const confirmed    = summary.confirmed     || 0;
  const falseAlarms  = summary.falseAlarms   || 0;
  const pending      = summary.pending       || 0;
  const avgAckTime   = summary.avgAckTimeMs  || 0;

  const byCategory    = data?.byCategory    || [];
  const bySeverity    = data?.bySeverity    || [];
  const byAlarmStatus = data?.byAlarmStatus || [];
  const byProvince    = data?.byProvince    || [];
  const byCity        = data?.byCity        || [];
  const byPanel       = data?.byPanel       || [];
  const byDay         = data?.byDay         || [];

  // ── render ──────────────────────────────────────────────────────────────────
  return (
    <div className="pt-16 min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* ── Header row ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <BarChart2 className="h-6 w-6 text-blue-600" />
            <h1 className="text-xl font-bold text-gray-900">Reporting</h1>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Period selector */}
            <div className="flex gap-1 bg-white border border-gray-200 rounded-lg p-1 shadow-sm">
              {PERIODS.map((p) => (
                <button
                  key={p.value}
                  onClick={() => setPeriod(p.value)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    period === p.value ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Filter toggle */}
            <button
              onClick={() => setShowFilters(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors shadow-sm ${
                activeCount > 0
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filters
              {activeCount > 0 && (
                <span className="bg-white text-blue-600 rounded-full text-xs font-bold w-5 h-5 flex items-center justify-center">
                  {activeCount}
                </span>
              )}
              {showFilters ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
          </div>
        </div>

        {/* ── Filter panel ── */}
        {showFilters && (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <SelectFilter
                label="Province"
                value={filters.province}
                options={options.provinces}
                onChange={(v) => setFilters(f => ({ ...f, province: v, city: '' }))}
              />
              <SelectFilter
                label="City"
                value={filters.city}
                options={options.cities.filter(c =>
                  !filters.province ||
                  (data?.byCity || []).find(r => r._id === c && r.province === filters.province)
                )}
                onChange={(v) => setFilters(f => ({ ...f, city: v }))}
              />
              <SelectFilter
                label="Panel ID"
                value={filters.accountNumber}
                options={options.panels}
                onChange={(v) => setFilters(f => ({ ...f, accountNumber: v }))}
                placeholder="All Panels"
              />
              <SelectFilter
                label="Category"
                value={filters.category}
                options={CATEGORIES}
                onChange={(v) => setFilters(f => ({ ...f, category: v }))}
              />
              <SelectFilter
                label="Severity"
                value={filters.severity}
                options={SEVERITIES}
                onChange={(v) => setFilters(f => ({ ...f, severity: v }))}
              />
              <SelectFilter
                label="Alarm Status"
                value={filters.alarmStatus}
                options={ALARM_STATUSES}
                onChange={(v) => setFilters(f => ({ ...f, alarmStatus: v }))}
                placeholder="All Statuses"
              />
            </div>
            {activeCount > 0 && (
              <div className="mt-3 flex justify-end">
                <button onClick={clearAll} className="text-sm text-red-500 hover:text-red-700 font-medium">
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Active filter chips ── */}
        {activeCount > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {activeFilters.map(([key, value]) => (
              <span
                key={key}
                className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full"
              >
                <span className="opacity-60">{FILTER_LABELS[key]}:</span> {value}
                <button onClick={() => clearFilter(key)} className="ml-0.5 hover:text-blue-600">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* ── Error ── */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        {/* ── Loading ── */}
        {loading && (
          <div className="text-center py-20 text-gray-500 text-sm">Loading report data…</div>
        )}

        {!loading && data && (
          <>
            {/* ── Summary cards ── */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
              <StatCard icon={BarChart2}     label="Total Events"     value={fmt(total)}        color="blue" />
              <StatCard icon={CheckCircle}   label="Acknowledged"     value={fmt(acknowledged)} sub={`${pct(acknowledged, total)}% of total`} color="green" />
              <StatCard icon={AlertTriangle} label="Confirmed Alarms" value={fmt(confirmed)}    sub={`${pct(confirmed, total)}% of total`}    color="red" />
              <StatCard icon={XCircle}       label="False Alarms"     value={fmt(falseAlarms)}  sub={`${pct(falseAlarms, total)}% of total`}  color="yellow" />
              <StatCard icon={Clock}         label="Pending"          value={fmt(pending)}      color="gray" />
              <StatCard icon={Clock}         label="Avg Ack Time"     value={fmtMs(avgAckTime)} color="blue" />
            </div>

            {/* ── Breakdown grid ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              {/* By Category */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                <SectionTitle icon={BarChart2} title="By Category" />
                <p className="text-xs text-gray-400 mb-2">Click a row to filter by that category</p>
                {byCategory.length === 0
                  ? <p className="text-sm text-gray-400">No data</p>
                  : byCategory.map((r) => (
                      <BarRow
                        key={r._id}
                        label={r._id}
                        count={r.count}
                        total={total}
                        colorClass={categoryColor(r._id)}
                        onDrillDown={filters.category === r._id ? null : () => drillDown('category', r._id)}
                      />
                    ))}
              </div>

              {/* By Severity */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                <SectionTitle icon={AlertTriangle} title="By Severity" />
                <p className="text-xs text-gray-400 mb-2">Click a row to filter by that severity</p>
                {bySeverity.length === 0
                  ? <p className="text-sm text-gray-400">No data</p>
                  : bySeverity.map((r) => (
                      <BarRow
                        key={r._id}
                        label={r._id}
                        count={r.count}
                        total={total}
                        colorClass={severityColor(r._id)}
                        onDrillDown={filters.severity === r._id ? null : () => drillDown('severity', r._id)}
                      />
                    ))}
              </div>

              {/* By Alarm Status */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                <SectionTitle icon={CheckCircle} title="By Alarm Status" />
                <p className="text-xs text-gray-400 mb-2">Click a row to filter by that status</p>
                {byAlarmStatus.length === 0
                  ? <p className="text-sm text-gray-400">No data</p>
                  : byAlarmStatus.map((r) => (
                      <BarRow
                        key={r._id}
                        label={r._id}
                        count={r.count}
                        total={total}
                        colorClass={statusColor(r._id)}
                        onDrillDown={filters.alarmStatus === r._id ? null : () => drillDown('alarmStatus', r._id)}
                      />
                    ))}
              </div>

              {/* By Province */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                <SectionTitle icon={MapPin} title="By Province" />
                <p className="text-xs text-gray-400 mb-2">Click a row to filter by that province</p>
                {byProvince.length === 0
                  ? <p className="text-sm text-gray-400">No data</p>
                  : byProvince.map((r) => (
                      <BarRow
                        key={r._id}
                        label={r._id}
                        count={r.count}
                        total={total}
                        colorClass="bg-teal-500"
                        onDrillDown={filters.province === r._id ? null : () => drillDown('province', r._id)}
                      />
                    ))}
              </div>
            </div>

            {/* ── By City ── */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mt-6">
              <SectionTitle icon={MapPin} title="By City" />
              <p className="text-xs text-gray-400 mb-2">Click a row to filter by that city</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                {byCity.length === 0
                  ? <p className="text-sm text-gray-400">No data</p>
                  : byCity.map((r) => (
                      <BarRow
                        key={`${r._id}-${r.province}`}
                        label={r._id ? `${r._id}${r.province ? ` (${r.province})` : ''}` : '—'}
                        count={r.count}
                        total={total}
                        colorClass="bg-cyan-500"
                        onDrillDown={filters.city === r._id ? null : () => drillDown('city', r._id)}
                      />
                    ))}
              </div>
            </div>

            {/* ── All Panels ── */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mt-6">
              <SectionTitle icon={Cpu} title={`All Panels (${byPanel.length})`} />
              <p className="text-xs text-gray-400 mb-3">Click a row to filter to that panel</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-100">
                      <th className="pb-2 pr-3 font-medium">#</th>
                      <th className="pb-2 pr-3 font-medium">Panel ID</th>
                      <th className="pb-2 pr-3 font-medium">Location</th>
                      <th className="pb-2 pr-3 font-medium">Province</th>
                      <th className="pb-2 pr-3 font-medium">City</th>
                      <th className="pb-2 pr-3 font-medium text-right">Events</th>
                      <th className="pb-2 pr-3 font-medium text-right">Ack'd</th>
                      <th className="pb-2 pr-3 font-medium text-right">Confirmed</th>
                      <th className="pb-2 font-medium text-right">False</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byPanel.length === 0
                      ? <tr><td colSpan={9} className="text-gray-400 py-3">No data</td></tr>
                      : byPanel.map((r, i) => {
                          const isActive = filters.accountNumber === r._id;
                          return (
                            <tr
                              key={r._id}
                              onClick={() => drillDown('accountNumber', r._id)}
                              className={`border-b border-gray-50 cursor-pointer transition-colors ${
                                isActive ? 'bg-blue-50' : 'hover:bg-gray-50'
                              }`}
                              title={`Filter to panel ${r._id}`}
                            >
                              <td className="py-2 pr-3 text-gray-400">{i + 1}</td>
                              <td className="py-2 pr-3 font-mono text-gray-800 font-medium">{r._id || '—'}</td>
                              <td className="py-2 pr-3 text-gray-500 text-xs max-w-xs truncate">{r.location || '—'}</td>
                              <td className="py-2 pr-3 text-gray-500 text-xs">{r.province || '—'}</td>
                              <td className="py-2 pr-3 text-gray-500 text-xs">{r.city || '—'}</td>
                              <td className="py-2 pr-3 text-right font-medium">{fmt(r.count)}</td>
                              <td className="py-2 pr-3 text-right text-green-700">{fmt(r.acknowledged)} <span className="text-gray-400 text-xs">({pct(r.acknowledged, r.count)}%)</span></td>
                              <td className="py-2 pr-3 text-right text-red-600">{fmt(r.confirmed)}</td>
                              <td className="py-2 text-right text-yellow-600">{fmt(r.falseAlarms)}</td>
                            </tr>
                          );
                        })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Daily trend ── */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mt-6 mb-8">
              <SectionTitle icon={Calendar} title="Daily Event Trend" />
              {byDay.length === 0
                ? <p className="text-sm text-gray-400">No data</p>
                : (() => {
                    const maxCount = Math.max(...byDay.map((d) => d.count), 1);
                    return (
                      <div className="overflow-x-auto">
                        <div className="flex items-end gap-1 min-w-max h-36 border-b border-gray-200 pb-1 pt-4">
                          {byDay.map((d) => (
                            <div key={d._id} className="flex flex-col items-center gap-0.5 group">
                              <span className="text-xs text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap font-medium">
                                {d.count}
                              </span>
                              <div
                                className="w-5 bg-blue-400 rounded-t hover:bg-blue-600 transition-colors cursor-default"
                                style={{ height: `${Math.max((d.count / maxCount) * 96, 2)}px` }}
                                title={`${d._id}: ${d.count} events`}
                              />
                              <span className="text-gray-400 rotate-45 origin-left whitespace-nowrap" style={{ fontSize: '9px' }}>
                                {d._id}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
